import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { BookmarkAddOutlined as SaveIcon, BookmarkBorder as SavedIcon, NotificationsActiveOutlined as AlertIcon } from '@mui/icons-material';
import { savedSearchesApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { SavedSearch, SavedSearchAlert } from '../../types';
import { errorMessage } from '../../utils/errors';
import { JobBoardState, sameQuery, savedQuery, suggestedSearchName } from '../../utils/jobBoard';

interface Props {
  state: JobBoardState;
  /** Open a saved search: its query string, without "?". */
  onOpen: (query: string) => void;
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
}

/**
 * The searches the user saved, as chips above the list, and "Save this
 * search" for the current one. A saved search is the board's query string,
 * so opening one restores every filter, the search words and the sort.
 */
const SavedSearches: React.FC<Props> = ({ state, onOpen, notify }) => {
  const [searches, setSearches] = useState<SavedSearch[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [alert, setAlert] = useState<SavedSearchAlert>('off');
  const [problem, setProblem] = useState<string | null>(null);
  const email = useAuthStore((store) => store.user?.email ?? '');

  useEffect(() => {
    const controller = new AbortController();
    savedSearchesApi
      .list(controller.signal)
      .then(setSearches)
      .catch((e) => {
        if (!controller.signal.aborted) setSearches([]);
        if (!controller.signal.aborted && (e as { response?: unknown })?.response) notify(errorMessage(e, 'Could not load your saved searches.'), 'error');
      });
    return () => controller.abort();
  }, [notify]);

  const query = useMemo(() => savedQuery(state), [state]);
  const current = useMemo(() => (searches ?? []).find((saved) => sameQuery(saved.query, query)) ?? null, [searches, query]);
  const canSave = query !== '';

  const startSaving = () => {
    setName(current?.name ?? suggestedSearchName(state));
    setAlert(current?.alert ?? 'off');
    setProblem(null);
    setNaming(true);
  };

  const save = async () => {
    const trimmed = name.replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      setProblem('Give the search a name.');
      return;
    }
    setSaving(true);
    try {
      const saved = current ? await savedSearchesApi.update(current.id, { name: trimmed, alert }) : await savedSearchesApi.save(trimmed, query, alert);
      setSearches((list) => [saved, ...(list ?? []).filter((item) => item.id !== saved.id)]);
      setNaming(false);
      notify(current ? 'Saved search updated.' : saved.alert === 'off' ? 'Search saved.' : `Search saved. New matches will be emailed ${saved.alert}.`);
    } catch (e) {
      setProblem(errorMessage(e, 'The search could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const open = useCallback(
    (saved: SavedSearch) => {
      onOpen(saved.query);
      setSearches((list) => (list ? [saved, ...list.filter((item) => item.id !== saved.id)] : list));
      savedSearchesApi.used(saved.id).catch(() => {
        /* the order is a nicety */
      });
    },
    [onOpen]
  );

  const remove = async (saved: SavedSearch) => {
    const before = searches;
    setSearches((list) => (list ? list.filter((item) => item.id !== saved.id) : list));
    try {
      await savedSearchesApi.remove(saved.id);
      notify(`"${saved.name}" removed from your saved searches.`);
    } catch (e) {
      setSearches(before);
      notify(errorMessage(e, 'The search could not be removed.'), 'error');
    }
  };

  if (searches === null) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }} role="group" aria-label="Saved searches">
        {searches.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.25 }}>
            Saved searches
          </Typography>
        )}
        {searches.map((saved) => {
          const active = current?.id === saved.id;
          return (
            <Chip
              key={saved.id}
              size="small"
              icon={saved.alert === 'off' ? <SavedIcon fontSize="small" /> : <AlertIcon fontSize="small" />}
              label={saved.name}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => (active ? startSaving() : open(saved))}
              onDelete={() => remove(saved)}
              title={`${active ? 'This search. Click to rename it or change its emails.' : `Open: ${saved.name}`}${saved.alert === 'off' ? '' : ` (${saved.alert} email)`}`}
              deleteIcon={<span aria-hidden="true">×</span>}
              aria-label={active ? `${saved.name} (this search), edit` : `Open saved search ${saved.name}`}
              sx={{ maxWidth: '100%', '& .MuiChip-deleteIcon': { fontSize: 16, lineHeight: 1, px: 0.5 } }}
            />
          );
        })}
        {!current && (
          // Always shown, so the feature can be found; with nothing but the defaults there is nothing to save yet.
          <Tooltip title={canSave ? 'Keep this search, and optionally get its new jobs by email' : 'Search for something or choose a filter first, then save it here'}>
            <span>
              <Button size="small" startIcon={<SaveIcon fontSize="small" />} onClick={startSaving} disabled={!canSave}>
                Save this search
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Dialog open={naming} onClose={saving ? undefined : () => setNaming(false)} fullWidth maxWidth="xs" aria-labelledby="save-search-title">
        <DialogTitle id="save-search-title">{current ? 'Edit this saved search' : 'Save this search'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {problem && <Alert severity="error">{problem}</Alert>}
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              fullWidth
              inputProps={{ maxLength: 80 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
              }}
            />
            <TextField
              select
              label="Email me new matches"
              value={alert}
              onChange={(e) => setAlert(e.target.value as SavedSearchAlert)}
              fullWidth
              helperText={alert === 'off' ? 'No emails. You can switch this on later.' : `A ${alert} digest of the new jobs goes to ${email || 'your account email'}, only when there are any. Every email has a link to stop.`}
            >
              <MenuItem value="off">Never</MenuItem>
              <MenuItem value="daily">Daily digest</MenuItem>
              <MenuItem value="weekly">Weekly digest</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary">
              The search words, filters and sort are kept. The page you are on and any open job are not.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNaming(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SavedSearches;
